import React, { createContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import * as Sentry from "@sentry/react";
import ReactGA from "react-ga4";
import { useDesignVersion } from "../../Utils/DesignVersionConfig";
import { trackData } from "../../Utils/attribution/CheckoutAttribution";
import V2CheckoutLayout from "./Components/V2/V2CheckoutLayout";

import "./CheckoutPage.css";
import CartSubtitle from "./Components/CartSubtitle/CartSubtitle";
import CheckoutFooter from "./Components/CheckoutFooter/CheckoutFooter";
import UniversalCheckoutHeader from "../../UniversalComponents/CheckoutHeader/CheckoutHeader";
import CheckoutPaymentFooter from "./Components/CheckoutPaymentFooter/CheckoutPaymentFooter";
import PaymentFailedModal from "./Components/PaymentFailed/PaymentFailedModal";
import SubtotalHeader from "./Components/SubtotalHeader/SubtotalHeader";

import {
  FinancialData,
  ItemDetailsData,
  RecommendedItem,
  SchoolDetails,
  DataLinks,
} from "../../Utils/DefinitionsInterfaces";
import { merchantLogoString } from "../../Utils/Constants";
import {
  doNFOTransactionResponse,
  fetchRecommendedItems,
  getSchoolByCode,
  verifyNFOTransaction,
  verifyNFOTransactionRazorpay,
} from "../../Utils/CheckoutAPIs/CheckoutAPIs";
import PFHModal from "./Components/PFHModal/PFHModal";

// Add type declarations for window
declare global {
  interface Window {
    loadBillDeskSdk: (config: any) => void;
  }
}

// Define proper types for context
interface UserContextType {
  userDetails: {
    name: string;
    email: string;
    school: string;
    schoolID: number;
    grade: number;
    mobileNumber: string;
    address: string;
    flatNo: string;
    city: string;
    state: string;
    pincode: string;
    pfh?: boolean;
  };
  setUserDetails: React.Dispatch<
    React.SetStateAction<UserContextType["userDetails"]>
  >;
  backendSchool: SchoolDetails;
  setBackendSchool: React.Dispatch<React.SetStateAction<SchoolDetails>>;
  schoolID: number;
  setSchoolID: React.Dispatch<React.SetStateAction<number>>;
  individualReg: boolean;
  setIndividualReg: React.Dispatch<React.SetStateAction<boolean>>;
  examDate: string;
  setExamDate: React.Dispatch<React.SetStateAction<string>>;
  isOtpVerified: boolean;
  setIsOtpVerified: React.Dispatch<React.SetStateAction<boolean>>;
  pfhVerified: boolean;
  setPfhVerified: React.Dispatch<React.SetStateAction<boolean>>;
  nfo2024Verified: boolean;
  setNfo2024Verified: React.Dispatch<React.SetStateAction<boolean>>;
}

// Create default context value to ensure context is never null
const createDefaultContextValue = (): UserContextType => ({
  userDetails: {
    name: "",
    email: "",
    school: "",
    schoolID: 0,
    grade: 0,
    mobileNumber: "",
    address: "",
    flatNo: "",
    city: "",
    state: "",
    pincode: "",
  },
  setUserDetails: () => {},
  backendSchool: {
    schoolID: 0,
    schoolName: "",
    linkActive: false,
    city: "",
    state: "",
    individual_registration: false,
    examDate: "",
    isInternational: false,
    version_no: "V1",
    pg: "razorpay",
    pincode: "560076",
  },
  setBackendSchool: () => {},
  schoolID: 0,
  setSchoolID: () => {},
  individualReg: false,
  setIndividualReg: () => {},
  examDate: "",
  setExamDate: () => {},
  isOtpVerified: false,
  setIsOtpVerified: () => {},
  pfhVerified: false,
  setPfhVerified: () => {},
  nfo2024Verified: false,
  setNfo2024Verified: () => {},
});

export const UserContext = createContext<UserContextType>(createDefaultContextValue());

interface CheckoutPageProps {
  history?: {
    push: (path: string) => void;
    replace: (path: string) => void;
  };
}

const CheckoutPage: React.FC<CheckoutPageProps> = ({ history }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const { version, config } = useDesignVersion();

  // Design version logic: V2 only when individualRegistration is true, otherwise V1

  // State management
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [verifyPaymentProgress, setVerifyPaymentProgress] = useState(false);
  const [isPaymentPageActive, setIsPaymentPageActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkoutItems, setCheckoutItems] = useState<ItemDetailsData[]>([]);
  const [recommendedItems, setRecommendedItems] = useState<RecommendedItem[]>(
    []
  );
  const [allItemDetailsDB, setAllItemDetailsDB] = useState<ItemDetailsData[]>(
    []
  );
  const [defaultItems, setDefaultItems] = useState<FinancialData[]>([]);
  const [checkoutItemIds, setCheckoutItemIds] = useState<number[]>([]);
  const [backendSchool, setBackendSchool] = useState<SchoolDetails>({
    schoolID: 0,
    schoolName: "",
    linkActive: false,
    city: "",
    state: "",
    individual_registration: false,
    examDate: "",
    isInternational: false,
    version_no: "V1",
    pg: "razorpay",
    pincode: "560076",
  });
  const [schoolID, setSchoolID] = useState(0);
  const [individualReg, setIndividualReg] = useState(false);
  const [examDate, setExamDate] = useState("");
  const sessionStorageMobileNumber = sessionStorage.getItem("mobileNumber");
  // Initialize userDetails from sessionStorage on mount
  const [userDetails, setUserDetails] = useState(() => {
    // Load from sessionStorage if available
    return {
      name: sessionStorage.getItem("name") || "",
      email: sessionStorage.getItem("email") || "",
      school: sessionStorage.getItem("school") || "",
      schoolID: Number(sessionStorage.getItem("schoolID") || 0),
      grade: Number(sessionStorage.getItem("grade") || 0),
      mobileNumber: sessionStorage.getItem("mobileNumber") || "",
      address: sessionStorage.getItem("address") || "",
      flatNo: sessionStorage.getItem("flatNo") || "",
      city: sessionStorage.getItem("city") || "",
      state: sessionStorage.getItem("state") || "",
      pincode: sessionStorage.getItem("pincode") || "",
    };
  });
  
  // Initialize isOtpVerified from sessionStorage on mount
  const [isOtpVerified, setIsOtpVerified] = useState(() => {
    return sessionStorage.getItem("isOtpVerified") === "true";
  });
  const [pfhVerified, setPfhVerified] = useState(false);
  const [nfo2024Verified, setNfo2024Verified] = useState(false);
  // referredBy comes from URL params or cookie (referral_code) - set in App.tsx setParams
  const referredBy = sessionStorage.getItem("referredBy");

  // URL parameters
  const orderId = urlParams.get("order_id");
  const itemIds = urlParams.get("item_ids");
  const schoolCode = urlParams.get("scl");
  const nfoCheckoutAccessToken = urlParams.get("token");
  const [showAlert, setShowAlert] = useState({
    isAlertVisible: false,
    alert: "",
  });

  // Store token if present
  useEffect(() => {
    if (nfoCheckoutAccessToken) {
      localStorage.setItem("nfoCheckoutAccessToken", nfoCheckoutAccessToken);
    }
  }, [nfoCheckoutAccessToken]);

  // Handle school code
  useEffect(() => {
    if (schoolCode && schoolCode.length > 1) {
      sessionStorage.setItem("schoolCode", schoolCode);
    } else {
      const searchParams = new URLSearchParams({ scl: "IRS" }).toString();
      sessionStorage.setItem("schoolCode", "IRS");
      const newUrl = `/CheckoutPage/?${searchParams}`;
      navigate(newUrl);
    }
  }, [schoolCode, navigate]);

  // Payment verification functions
  const verifyPaymentInternational = async (
    orderId: string
  ): Promise<boolean> => {
    try {
      const response = await fetch(
        "https://payments-api.nationalfinanceolympiad.com/nfo/stripe-metadata",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ sessionId: orderId }),
        }
      );

      const data = await response.json();
      return (
        data.data.message ===
        "Metadata retrieved, student onboarded, and invoice sent successfully."
      );
    } catch (err) {
      Sentry.captureException(err);
      return false;
    }
  };

  console.log("is otp verified", isOtpVerified);

  const verifyPayment = async (orderId: string): Promise<boolean> => {
    try {
      const errorStatuses = [
        "payment_processing_error",
        "user_cancelled_error",
        "acquirer_processing_error",
        "risk_validation_error",
      ];

      const retryPayment = async (
        remainingRetries: number
      ): Promise<boolean> => {
        while (remainingRetries > 0) {
          const newResponse = await verifyNFOTransaction(orderId);
          const newPaymentStatus = newResponse.data.links[0].method;

          if (newPaymentStatus === "success") return true;
          if (newPaymentStatus === "retry") {
            remainingRetries--;
            await new Promise((resolve) => setTimeout(resolve, 1000));
          } else if (errorStatuses.includes(newPaymentStatus)) return false;
          else return false;
        }
        return false;
      };

      const response = await verifyNFOTransaction(orderId);
      const paymentStatus = response.data.links[0].method;

      if (paymentStatus === "success") return true;
      if (paymentStatus === "retry") return await retryPayment(3);
      if (errorStatuses.includes(paymentStatus)) return false;
      return false;
    } catch (err) {
      Sentry.captureException(err);
      return false;
    }
  };

  // Add missing verifyPaymentRazorpay function
  const verifyPaymentRazorpay = async (orderId: string): Promise<boolean> => {
    try {
      const retryPayment = async (
        remainingRetries: number
      ): Promise<boolean> => {
        while (remainingRetries > 0) {
          const newResponse = await verifyNFOTransactionRazorpay(orderId);
          const newPaymentStatus = newResponse.data.status;

          if (newPaymentStatus === "paid") return true;
          if (newPaymentStatus === "created") {
            remainingRetries--;
            await new Promise((resolve) => setTimeout(resolve, 1000));
          } else if (newPaymentStatus === "attempted") return false;
          else return false;
        }
        return false;
      };

      const response = await verifyNFOTransactionRazorpay(orderId);
      const paymentStatus = response.data.status;

      if (paymentStatus === "paid") return true;
      if (paymentStatus === "created") return await retryPayment(3);
      if (paymentStatus === "attempted") return false;
      return false;
    } catch (err) {
      Sentry.captureException(err);
      return false;
    }
  };

  // Add paymentCallbackFunctionInternational
  const paymentCallbackFunctionInternational = async () => {
    if (!orderId) return;

    setVerifyPaymentProgress(true);
    try {
      const orderStatus = await verifyPaymentInternational(orderId);

      if (orderStatus) {
        setVerifyPaymentProgress(false);
        navigate(`/PaymentSuccessful/?&scl=${schoolCode}&order_id=${orderId}`);
        return;
      }

      // Retry verification
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const orderStatus2ndTime = await verifyPayment(orderId);

      if (orderStatus2ndTime) {
        setVerifyPaymentProgress(false);
        navigate(
          `/PaymentSuccessful/?&scl=${schoolCode}&order_id=${orderId}${
            itemIds ? `&item_ids=${itemIds}` : ""
          }`
        );
        return;
      }

      // Final retry
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const orderStatus3rdTime = await verifyPayment(orderId);

      if (orderStatus3rdTime) {
        setVerifyPaymentProgress(false);
        navigate(
          `/PaymentSuccessful/?&scl=${schoolCode}&order_id=${orderId}${
            itemIds ? `&item_ids=${itemIds}` : ""
          }`
        );
        return;
      }

      // Final verification with external API
      const response = await fetch(
        "https://payments-api.nationalfinanceolympiad.com/nfo/stripe-metadata",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ sessionId: orderId }),
        }
      );

      const data = await response.json();
      if (
        data.data.message !==
        "Metadata retrieved and student onboarded successfully"
      ) {
        setPaymentFailed(true);
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setVerifyPaymentProgress(false);
    }
  };

  // Payment callback handling
  const paymentCallbackFunction = async () => {
    if (!orderId) return;

    const pg = sessionStorage.getItem("pg");
    setVerifyPaymentProgress(true);

    const navigateToSuccess = () => {
      setVerifyPaymentProgress(false);
      navigate(
        `/PaymentSuccessful/?&scl=${schoolCode}&order_id=${orderId}${
          itemIds ? `&item_ids=${itemIds}` : ""
        }`
      );
    };

    const verifyAndNavigate = async (retries: number, delay: number) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        const orderStatus =
          pg === "razorpay"
            ? await verifyPaymentRazorpay(orderId)
            : await verifyPayment(orderId);

        if (orderStatus) {
          navigateToSuccess();
          return;
        }

        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    };

    try {
      await verifyAndNavigate(3, 2000);

      const orderStatus =
        pg === "razorpay"
          ? await verifyPaymentRazorpay(orderId)
          : await verifyPayment(orderId);

      if (orderStatus) {
        navigateToSuccess();
      } else {
        setPaymentFailed(true);
        Sentry.captureMessage("Final verification failed - Other Issue");
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setVerifyPaymentProgress(false);
    }
  };

  // Cart management functions
  const populateDefaultItems = (defaultItem: FinancialData) => {
    setDefaultItems([defaultItem]);
  };

  const getAndSetAllItemsDB = async (
    schoolCode: string,
    mobileNumber: string,
    pfh: boolean
  ): Promise<ItemDetailsData[]> => {
    try {
      const rollNumber = sessionStorage.getItem("rollNumber");
      const nfo2024Param = sessionStorage.getItem("nfo2024");
      const tempAllItems = await fetchRecommendedItems(
        schoolCode,
        mobileNumber,
        pfh,
        referredBy,
        rollNumber,
        nfo2024Param === "true"
      );

      console.log("🔍 API Response for NFO2024:", {
        nfo2024Param,
        defaultItem: tempAllItems.default,
        pfhPrice: tempAllItems.default.pfhPrice,
        nfo2024Price: tempAllItems.default.nfo2024Price,
        selling_price: tempAllItems.default.selling_price,
        base_price: tempAllItems.default.base_price,
      });

      const allItems = tempAllItems.recommendedItems.filter(
        (item) => item.selling_price !== 0
      );
      populateDefaultItems(tempAllItems.default);
      setAllItemDetailsDB(allItems);
      return allItems;
    } catch (err) {
      Sentry.captureException(err);
      throw new Error(`Failed to fetch items: ${err}`);
    }
  };

  const populateFullCart = (
    allItems: ItemDetailsData[],
    itemsIdsAr: number[]
  ) => {
    const tempCheckItems = allItems.filter((item) =>
      itemsIdsAr.includes(item.id)
    );
    const tempRecoItems = allItems.filter(
      (item) => !itemsIdsAr.includes(item.id)
    );
    setCheckoutItems(tempCheckItems);
    setRecommendedItems(tempRecoItems);
  };

  const handleRemoveItem = (itemId: number) => {
    setCheckoutItemIds((prevItems) =>
      prevItems.filter((currId) => currId !== itemId)
    );
    setCheckoutItems((prevItems) =>
      prevItems.filter((item) => item.id !== itemId)
    );

    const removedItem = allItemDetailsDB.find((item) => item.id === itemId);
    if (removedItem) {
      setRecommendedItems((prevItems) => [...prevItems, removedItem]);
    }
  };

  const handleAddItemFromRecommended = (itemId: number) => {
    setCheckoutItemIds((prevIds) => [...prevIds, itemId]);
    setRecommendedItems((prevItems) =>
      prevItems.filter((item) => item.id !== itemId)
    );

    const addedItem = allItemDetailsDB.find((item) => item.id === itemId);
    if (addedItem) {
      setCheckoutItems((prevItems) => [...prevItems, addedItem]);
    }
  };

  // Initialize school and items
  useEffect(() => {
    if (schoolCode && !individualReg) {
      setLoading(true);
      getSchoolByCode(schoolCode)
        .then((schoolDetails) => {
          if (schoolDetails && schoolDetails.schoolID !== 0) {
            if (!schoolDetails.linkActive) {
              navigate(`/LinkUnavailable/?&scl=${schoolCode}`);
              return;
            }
            setBackendSchool(schoolDetails);
            sessionStorage.setItem(
              "school",
              `${schoolDetails.schoolName}, ${schoolDetails.city}`
            );
            sessionStorage.setItem("pg", schoolDetails.pg);
            sessionStorage.setItem(
              "isInternationalTransaction",
              JSON.stringify(schoolDetails.isInternational)
            );
            sessionStorage.setItem(
              "school",
              JSON.stringify(schoolDetails.schoolName)
            );
            setSchoolID(schoolDetails.schoolID);
            sessionStorage.setItem(
              "schoolID",
              JSON.stringify(schoolDetails.schoolID)
            );
            setIndividualReg(schoolDetails.individual_registration);
            sessionStorage.setItem(
              "individual_registration",
              JSON.stringify(schoolDetails.individual_registration)
            );
            sessionStorage.setItem("examDate", schoolDetails.examDate);
            console.log(schoolDetails.pincode);
            sessionStorage.setItem("pincode", schoolDetails.pincode);

            if (schoolDetails.isInternational) {
              paymentCallbackFunctionInternational();
            } else {
              paymentCallbackFunction();
            }
          }
        })
        .then(() => {
          // Use decoded PFH value from sessionStorage for initial API call
          const pfhParam = sessionStorage.getItem("pfh");
          const isPfhRequested = pfhParam === "true";
          getAndSetAllItemsDB(
            schoolCode,
            userDetails.mobileNumber,
            isPfhRequested
          )
            .then((allItems) => {
              setCheckoutItemIds([]);
              populateFullCart(allItems, []);
              setLoading(false);
            })
            .catch((err) => {
              Sentry.captureException(err);
              setLoading(false);
            });
        })
        .catch((err) => {
          Sentry.captureException(err);
          setLoading(false);
        });
    }
  }, [schoolCode, navigate, individualReg]);

  // Handle PFH verification failure - revert to standard pricing
  useEffect(() => {
    const pfhParam = sessionStorage.getItem("pfh");
    const pfhVerified = sessionStorage.getItem("pfhVerified");

    // If PFH was requested but verification failed, reload with standard pricing
    if (schoolCode && pfhParam === "true" && pfhVerified === "false") {
      console.log("PFH verification failed - reloading with standard pricing");
      getAndSetAllItemsDB(schoolCode, userDetails.mobileNumber, false)
        .then((allItems) => {
          setCheckoutItemIds([]);
          populateFullCart(allItems, []);
          setLoading(false);
        })
        .catch((err) => {
          Sentry.captureException(err);
          setLoading(false);
        });
    }
  }, [schoolCode, userDetails.mobileNumber, pfhVerified]);

  useEffect(() => {
    if (schoolCode && pfhVerified) {
      console.log(
        "PFH verification successful - reloading items with PFH pricing",
        schoolCode,
        userDetails.mobileNumber,
        pfhVerified
      );
      getAndSetAllItemsDB(schoolCode, userDetails.mobileNumber, pfhVerified)
        .then((allItems) => {
          setCheckoutItemIds([]);
          populateFullCart(allItems, []);
          setLoading(false);
        })
        .catch((err) => {
          Sentry.captureException(err);
          setLoading(false);
        });
    }
  }, [schoolCode, pfhVerified]);

  // Payment handling
  const handlePaymentClickNFO = async (
    data?: doNFOTransactionResponse | any
  ) => {
    if (backendSchool.isInternational) {
      setLoading(true);
      navigate("/PaymentPage");
      return;
    }

    const sellingPrice = defaultItems[0]?.selling_price;
    // sessionStorage.setItem(
    //   "transactionAmount",
    //   sellingPrice?.toString() || "0"
    // );

    const itemIdsString = checkoutItems.map((item) => item.id).join("-");
    const pg = sessionStorage.getItem("pg") || "razorpay";

    try {
      setLoading(true);

      // Handle Razorpay payment
      if (pg === "razorpay") {
        console.log("Handling Razorpay payment with data:", data);

        // Check if we have the new API response structure
        // The API response has: data.message.id for the order ID
        if (
          data &&
          (data.orderID || data.order_id || (data.message && data.message.id))
        ) {
          // New API response structure - order ID can be in multiple places
          const orderId =
            data.orderID || data.order_id || (data.message && data.message.id);
          const transactionAmount =
            sessionStorage.getItem("transactionAmount") || "0";

          console.log(
            "Order ID:",
            orderId,
            "Transaction Amount:",
            transactionAmount
          );

          // Open Razorpay Checkout
          const options = {
            key: "rzp_live_B6xhot5HoXKLQE", // Replace with your Razorpay API Key
            amount: parseInt(transactionAmount) * 100, // Amount in paise
            currency: "INR",
            name: "National Finance Olympiad & Study Material",
            description: "National Finance Olympiad & Study Material",
            image: "https://cdn.razorpay.com/logos/BUVwvgaqVByGp2_large.jpg",
            order_id: orderId,
            prefill: {
              name: userDetails.name,
              email: userDetails.email,
              contact: userDetails.mobileNumber,
            },
            theme: {
              color: "rgba(55, 108, 170, 1)",
            },
            method: {
              netbanking: true,
              card: true,
              wallet: false,
              upi: true,
              paylater: false,
            },
            handler: async (response: any) => {
              console.log("Razorpay payment successful:", response);
              // Store payment details for verification
              sessionStorage.setItem(
                "razorpay_payment_id",
                response.razorpay_payment_id
              );
              sessionStorage.setItem(
                "razorpay_signature",
                response.razorpay_signature
              );
              sessionStorage.setItem("razorpay_order_id", orderId);

              // Navigate to success page
              navigate(
                `/PaymentSuccessful/?&scl=${
                  schoolCode ?? ""
                }&order_id=${orderId}&${
                  checkoutItems.length > 0 ? `item_ids=${itemIdsString}` : ""
                }`
              );
            },
            modal: {
              ondismiss: function () {
                console.log("Razorpay modal dismissed");
              },
            },
          };

          console.log("Razorpay options:", options);

          // Check if Razorpay is loaded
          if (typeof window !== "undefined" && (window as any).Razorpay) {
            console.log("Razorpay already loaded, opening modal...");
            const razorpay = new (window as any).Razorpay(options);
            razorpay.open();
          } else {
            console.log("Loading Razorpay script...");
            // Load Razorpay script if not already loaded
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => {
              console.log("Razorpay script loaded, opening modal...");
              const razorpay = new (window as any).Razorpay(options);
              razorpay.open();
            };
            script.onerror = (error) => {
              console.error("Failed to load Razorpay script:", error);
            };
            document.head.appendChild(script);
          }
          return;
        } else {
          console.error("Invalid data structure for Razorpay:", data);
          throw new Error("Invalid order data for Razorpay payment");
        }
      }

      // Handle BillDesk payment (existing logic)
      if (!data?.data?.links?.[1]?.parameters?.bdorderid) {
        throw new Error("Invalid payment data for BillDesk");
      }

      const flow_config = {
        merchantId: data.data.links[1].parameters.mercid,
        bdOrderId: data.data.links[1].parameters.bdorderid,
        authToken: data.data.links[1].headers.authorization,
        childWindow: true,
        returnUrl: `https://nfoself.checkout.nationalfinanceolympiad.com/CheckoutPage/?&scl=${schoolCode}&order_id=${data.data.orderID}&item_ids=${itemIdsString}`,
        retryCount: 3,
        prefs: {
          payment_categories: ["card", "nb", "upi"],
        },
        netBanking: {
          showPopularBanks: "Y",
          popularBanks: ["Kotak Bank", " AXIS Bank [Retail]"],
        },
      };

      const responseHandler = (txn: { status: string; response: string }) => {
        Sentry.captureMessage(
          `Payment callback - Status: ${txn.status}, Response: ${txn.response}`,
          "info"
        );
      };

      const theme_config = {
        sdkPrimaryColor: "#023b8f",
        sdkAccentColor: "#023b8f",
        sdkBackgroundColor: "#FFF",
        sdkBannerColor: "#FFF",
      };

      const config = {
        responseHandler,
        merchantLogo: merchantLogoString,
        flowConfig: flow_config,
        flowType: "payments",
        themeConfig: theme_config,
      };

      if (window.loadBillDeskSdk) {
        window.loadBillDeskSdk(config);
      }
    } catch (err) {
      Sentry.captureException(err);
      console.error("Payment error:", err);
      throw new Error(`Payment Error: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  const isPfh = urlParams.get("pfh");
  console.log("isPfh", isPfh);
  const decodedPfh = atob(isPfh || "");
  const [isPfhModalOpen, setIsPfhModalOpen] = useState(
    decodedPfh === "true" ? true : false
  );
  console.log("isPfhModalOpen", isPfhModalOpen, isPfh);
  console.log("userDetails", userDetails.mobileNumber);

  // In CheckoutPage.tsx, add a function to track form completion
  const trackFormCompletion = () => {
    const requiredFields = {
      name: userDetails.name,
      email: userDetails.email,
      mobileNumber: userDetails.mobileNumber,
      address: userDetails.address,
      flatNo: userDetails.flatNo,
      city: userDetails.city,
      state: userDetails.state,
      pincode: userDetails.pincode,
    };

    const isFormComplete = Object.values(requiredFields).every(
      (field) => field !== ""
    );

    if (isFormComplete) {
      trackData("5678_checkout_fields_filled");
      ReactGA.event({
        category: "5678_checkout_fields_filled",
        action: "5678_checkout_fields_filled",
        label: "5678_checkout_fields_filled",
      });
    }
  };

  // Add useEffect to monitor form completion
  useEffect(() => {
    trackFormCompletion();
  }, [userDetails]);

  // Persist userDetails to sessionStorage whenever they change
  useEffect(() => {
    Object.entries(userDetails).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        sessionStorage.setItem(key, String(value));
      }
    });
  }, [userDetails]);

  // Persist isOtpVerified to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem("isOtpVerified", String(isOtpVerified));
  }, [isOtpVerified]);

  return (
    <div
      className={`checkout ${
        backendSchool.individual_registration ? "v2" : ""
      }`}
      style={{ backgroundColor: config.styling.backgroundColor }}
    >
      {/* Always render UserContext.Provider to ensure context is never null */}
      <UserContext.Provider
        value={{
          userDetails,
          setUserDetails,
          backendSchool,
          setBackendSchool,
          schoolID,
          setSchoolID,
          individualReg,
          setIndividualReg,
          examDate,
          setExamDate,
          isOtpVerified,
          setIsOtpVerified,
          pfhVerified,
          setPfhVerified,
          nfo2024Verified,
          setNfo2024Verified,
        }}
      >
        {verifyPaymentProgress || loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "#FFF",
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <>
            {/* 
              Design Logic: 
              - V2 design: Only when individualRegistration is true
              - V1 design: Default for school-based registrations
            */}
            {backendSchool.individual_registration ? (
              <V2CheckoutLayout
                stateCheckoutItems={checkoutItems}
                stateDefaultItems={defaultItems}
                stateRecommendedItems={recommendedItems}
                cbRemoveProduct={handleRemoveItem}
                cbAddRecommended={handleAddItemFromRecommended}
                cbHandlePaymentClick={handlePaymentClickNFO}
              />
            ) : (
              <>
                <UniversalCheckoutHeader />
                <SubtotalHeader />
                <CartSubtitle
                  stateCheckoutItems={checkoutItems}
                  stateDefaultItems={defaultItems}
                  stateRecommendedItems={recommendedItems}
                  cbRemoveProduct={handleRemoveItem}
                  cbAddRecommended={handleAddItemFromRecommended}
                  cbHandlePaymentClick={handlePaymentClickNFO}
                />
                <CheckoutPaymentFooter />
                <CheckoutFooter />
              </>
            )}
          </>
        )}
      </UserContext.Provider>

      <PaymentFailedModal
        paymentFailed={paymentFailed}
        setPaymentFailed={setPaymentFailed}
      />
      {/* Only show PFH modal for V1 design (school-based registrations) */}
      {/* {isPfhModalOpen && !backendSchool.individual_registration && (
        <PFHModal
          onVerificationFailed={(mobileNumber) => {
            setPfhVerified(false);
            setIsOtpVerified(false);
            setUserDetails((prev) => ({
              ...prev,
              mobileNumber,
              pfh: false,
            }));
          }}
          onClose={() => setIsPfhModalOpen(false)}
          isOpen={isPfhModalOpen}
          onVerificationSuccess={(mobileNumber, pfhVerified) => {
            if (!pfhVerified) {
              setShowAlert({
                isAlertVisible: true,
                alert:
                  "You've used your discount for the National Finance Olympiad 2026-27. Please proceed with standard pricing.",
              });
            }
            console.log("mobileNumber", mobileNumber);
            setPfhVerified(true);
            setIsOtpVerified(true);
            setUserDetails((prev) => ({
              ...prev,
              mobileNumber,
              pfh: true,
            }));
            setIsPfhModalOpen(false);
          }}
          isPFH={Boolean(isPfh)}
        />
      )} */}

      {showAlert.alert && (
        <Alert
          style={{
            borderRadius: 10,
            opacity: 1,
            position: "fixed",
            top: 5,
            left: 0,
            right: 0,
            backgroundColor: "#FFF",
            maxWidth: "380px",
            margin: "auto",
            maxHeight: "100px",
            alignItems: "center",
            animation:
              "springInFromTop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            zIndex: 1000,
          }}
          severity="error"
        >
          {showAlert.alert}
        </Alert>
      )}
    </div>
  );
};

export default CheckoutPage;
